import React from "react";
import { Link } from "react-router-dom";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import Input from "../../ui/Input";
import { deleteGuideline, listGuidelineUsage, listGuidelines, uploadGuidelines } from "../../api/build";

function alphaGuidelines(rows) {
  return [...rows].sort((a, b) => {
    const left = String(a?.filename || "").toLowerCase();
    const right = String(b?.filename || "").toLowerCase();
    if (left < right) return -1;
    if (left > right) return 1;
    return String(a?.id || "").localeCompare(String(b?.id || ""));
  });
}

const PAGE_SIZE = 10;

export default function GuidelinesLibrary() {
  const [tab, setTab] = React.useState("library");
  const [query, setQuery] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [usageByDocId, setUsageByDocId] = React.useState({});
  const [sortBy, setSortBy] = React.useState("alpha");
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [file, setFile] = React.useState(null);
  const fileInputRef = React.useRef(null);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [out, usage] = await Promise.all([listGuidelines(), listGuidelineUsage()]);
      const guidelines = Array.isArray(out?.guidelines) ? out.guidelines : [];
      setRows(alphaGuidelines(guidelines));
      setUsageByDocId(usage || {});
    } catch (e) {
      setError(e?.message || "Failed to load guidelines");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const filtered = rows.filter((r) => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return true;
    return (
      String(r?.filename || "").toLowerCase().includes(q) ||
      String(r?.id || "").toLowerCase().includes(q)
    );
  });
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "last_used") {
      const aUsed = String(usageByDocId?.[a.id]?.last_used_at || "");
      const bUsed = String(usageByDocId?.[b.id]?.last_used_at || "");
      if (aUsed && bUsed && aUsed !== bUsed) return bUsed.localeCompare(aUsed);
      if (aUsed && !bUsed) return -1;
      if (!aUsed && bUsed) return 1;
    }
    return String(a?.filename || "").localeCompare(String(b?.filename || ""), undefined, { sensitivity: "base" });
  });
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const clampedPage = Math.min(Math.max(1, page), totalPages);
  const paged = sorted.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE);

  React.useEffect(() => {
    setPage(1);
  }, [query, sortBy, rows.length]);

  const handleUpload = async () => {
    if (!file) {
      setError("Select a guideline file first.");
      return;
    }
    setBusy("upload");
    setError("");
    setNotice("");
    try {
      await uploadGuidelines("guideline-library", file);
      setNotice(`Uploaded: ${file.name}`);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await refresh();
      setTab("library");
    } catch (e) {
      setError(e?.message || "Failed to upload guideline");
    } finally {
      setBusy("");
    }
  };

  const handleDelete = async (id) => {
    const docId = String(id || "").trim();
    if (!docId) return;
    setBusy(`delete:${docId}`);
    setError("");
    setNotice("");
    try {
      await deleteGuideline(docId);
      setNotice("Guideline deleted.");
      await refresh();
    } catch (e) {
      setError(e?.message || "Failed to delete guideline");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <Card>
        <CardHeader>
          <h1 className="text-xl font-semibold text-slate-900">Estimating Guidelines</h1>
          <p className="mt-1 text-sm text-slate-500">
            Access uploaded guideline documents in one searchable library.
          </p>
        </CardHeader>
        <CardBody className="space-y-4">
          {error ? <p className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</p> : null}
          {notice ? <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</p> : null}

          <div className="flex flex-wrap gap-2">
            <Button variant={tab === "library" ? "primary" : "secondary"} onClick={() => setTab("library")}>
              Library
            </Button>
            <Button variant={tab === "upload" ? "primary" : "secondary"} onClick={() => setTab("upload")}>
              Upload
            </Button>
            <Link to="/app/build">
              <Button variant="ghost">Back to Build</Button>
            </Link>
          </div>

          {tab === "library" ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="min-w-[260px]"
                  placeholder="Search guidelines by name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <select
                  className="h-10 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="alpha">Sort: Alphabetical (A-Z)</option>
                  <option value="last_used">Sort: Last Used</option>
                </select>
                <Button variant="secondary" onClick={refresh} disabled={loading || !!busy}>
                  Refresh
                </Button>
              </div>
              {loading ? <p className="text-sm text-slate-500">Loading guidelines...</p> : null}
              {!loading ? (
                <>
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-slate-600">Filename (A-Z)</th>
                          <th className="px-3 py-2 text-left text-slate-600">Created</th>
                          <th className="px-3 py-2 text-left text-slate-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paged.length < 1 ? (
                          <tr>
                            <td colSpan={3} className="px-3 py-4 text-slate-500">No guidelines found.</td>
                          </tr>
                        ) : (
                          paged.map((row) => (
                            <tr key={row.id} className="border-t border-slate-100">
                              <td className="px-3 py-2 text-slate-900">{row.filename}</td>
                              <td className="px-3 py-2 text-slate-600">
                                {usageByDocId?.[row.id]?.last_used_at
                                  ? `Last used ${new Date(usageByDocId[row.id].last_used_at).toLocaleString()}`
                                  : row.created_at
                                    ? `Uploaded ${new Date(row.created_at).toLocaleString()}`
                                    : "-"}
                              </td>
                              <td className="px-3 py-2">
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleDelete(row.id)}
                                  disabled={busy === `delete:${row.id}`}
                                >
                                  {busy === `delete:${row.id}` ? "Deleting..." : "Delete"}
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      Page {clampedPage} of {totalPages} ({sorted.length} total)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={clampedPage <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={clampedPage >= totalPages}
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Upload a PDF, DOC, DOCX, or TXT guideline file.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt,text/plain,application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              {file ? <p className="text-sm text-slate-700">Selected: {file.name}</p> : null}
              <div className="flex flex-wrap gap-2">
                <Button onClick={handleUpload} disabled={busy === "upload" || !file}>
                  {busy === "upload" ? "Uploading..." : "Upload Guideline"}
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
